import moment from 'moment';
import Fuse from 'fuse.js';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { EJSON } from 'meteor/ejson';
import { MILLIS_1_YEAR, MILLIS_24_HOURS } from '/imports/constants';
import { DateTime } from 'luxon';
import { stringify } from 'csv-stringify/sync';
import { dateTimeFormatOptions } from '/imports/options';
import { Dict, Docmnt, RefMap, ModelClass } from '/imports/types';
import { http } from '/imports/http';
import { geoIp } from '/imports/api/miscl';

export const environments = {
  development: 'https://development2.luminia.io',
  staging: 'https://staging2.luminia.io',
  production: 'https://app2.luminia.io',
};

/**
 * Generates a random alphanumeric string.
 * @param n - desired length
 * @returns the random string
 */
export const randomString = (n = 8) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < n; i++) result += characters.charAt(Math.floor(Math.random() * charactersLength));
  return result;
};

/**
 * Generates a v4 UUID string.
 * @returns the UUID
 */
export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Appends an error message under a key in an errors map.
 * @param errors - keyed map of error message arrays
 * @param key - field key
 * @param error - error message
 * @returns false
 */
export const addError = (errors: { [key: string]: string[] }, key: string, error: string) => {
  errors[key] = errors[key] || [];
  errors[key].push(error);
  return false;
};

/**
 * Returns a new object with only the given keys copied from the source.
 * @param obj - source object
 * @param keys - keys to pick
 * @returns the picked subset
 */
export const pluck = <T extends object, K extends (keyof T)>(obj: T, keys: K[]) => {
  const plucked: any = {};
  for (const key of keys) {
    if (key in obj) plucked[key] = obj[key];
  }
  return plucked as Pick<T, K>;
};

/**
 * Sets a property on an object.
 * @param obj - target object
 * @param key - property key
 * @param value - value to set
 */
export const setProp = (obj: any, key: string, value: any) => {
  obj[key] = value;
};

/** Object.keys wrapper. */
export const objectKeys = (obj: any) => {
  return Object.keys(obj);
};

/** Number of own keys on an object. */
export const numKeys = (obj: any) => {
  return objectKeys(obj).length;
};

/** Object.values wrapper. */
export const objectValues = <T>(obj: { [key: string]: T }) => {
  return Object.values(obj);
};

/** Object.entries wrapper. */
export const objectEntries = <T>(obj: { [key: string]: T }) => {
  return Object.entries(obj);
};

/** Object.assign wrapper. */
export const objectAssign = <T extends object, U>(obj1: T, obj2: U) => {
  return Object.assign(obj1, obj2);
};

/**
 * Reports whether a value is a primitive (or null).
 * @param val - value to test
 * @returns true if primitive
 */
export const isPrimitive = (val: any) => {
  if (val === null) return true;
  if (typeof val == 'object' || typeof val == 'function') return false;
  else return true;
};

/**
 * Formats a date as a local datetime-input value (YYYY-MM-DDThh:mm).
 * @param date - the date
 * @returns the input-compatible string
 */
export const dateToInputString = (date: Date) => {
  const tzDate = new Date(date.getTime());
  tzDate.setMinutes(tzDate.getMinutes() - date.getTimezoneOffset());
  return tzDate.toISOString().substring(0, 16);
};

/**
 * Defers a function to the next tick.
 * @param func - function to run
 */
export const defer = (func: () => void) => {
  setTimeout(func, 0);
};

/**
 * Runs a function once a condition becomes true, polling until a timeout.
 * @param func - function to run
 * @param cond - condition to wait for
 * @param interval - poll interval in ms
 * @param timeout - max wait in ms
 */
export const deferUntil = (func: () => void, cond: () => boolean | undefined, interval = 10, timeout = 3000) => {
  const start = Date.now();
  let deferring = false;
  const _interval = setInterval(() => {
    if (Date.now() - start > timeout) clearInterval(_interval);
    if (deferring) return;
    deferring = true;
    if (cond()) {
      clearInterval(_interval);
      func();
    }
    deferring = false;
  }, interval);
  setTimeout(func, 0);
};

/**
 * Formats a date and time using the user's preferred format and timezone.
 * @param date - the date
 * @param format - luxon format token (defaults to user preference)
 * @param zone - timezone (defaults to user timezone)
 * @returns the formatted string, or '' if no date
 */
/**
 * A money amount as a person typed it. Blank means "not set"; a currency symbol, thousands separators and
 * surrounding space are tolerated; anything else is REJECTED rather than becoming NaN — `Number('abc')` and
 * `Number('1,000')` are both NaN, and a NaN satisfies Meteor's `Match.Number`, so an unchecked amount used
 * to reach the database and render as "$NaN".
 * @param text - the typed value
 * @returns ok:false when the text is not a number; otherwise the value, absent for a blank
 */
export const parseTypedAmount = (text?: string | null): { ok: boolean, value?: number } => {
  if (text === undefined || text === null) return { ok: true };
  const cleaned = String(text).trim().replace(/[$,\s]/g, '');
  if (!cleaned) return { ok: true };
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return { ok: false };
  return { ok: true, value };
};

/** How wide the buckets of a time series should be for a chart to stay drawable. */
export type IntervalGranularity = '15min' | 'hourly' | 'daily' | 'monthly';

/**
 * Picks a bucket width for a range, so a chart over a year draws twelve bars rather than 35,000. The
 * thresholds match the ones the RIC dashboard's system chart has used since it shipped.
 * @param from - range start
 * @param to - range end
 * @returns the granularity to read the range at
 */
export const intervalGranularityFor = (from: Date, to: Date): IntervalGranularity => {
  const delta = to.getTime() - from.getTime();
  if (delta > MILLIS_1_YEAR / 4) return 'monthly';
  if (delta > 7 * MILLIS_24_HOURS) return 'daily';
  if (delta > 2 * MILLIS_24_HOURS) return 'hourly';
  return '15min';
};

export const formatDateTime = (date?: Date, format?: string, zone?: string) => {
  if (!date) return '';
  if (!format) format = Meteor.user()?.dateTimeFormat || dateTimeFormatOptions()[0].value;
  if (!format) format = dateTimeFormatOptions()[0].value;
  if (!zone) zone = Meteor.user()?.timezone;
  return DateTime.fromJSDate(date, { zone }).toFormat(format);
};

/**
 * Formats the date portion using the user's preferred format and timezone.
 * @param date - the date
 * @param format - datetime format token (date portion is derived)
 * @param zone - timezone (defaults to user timezone)
 * @returns the formatted date, or '' if no date
 */
export const formatDate = (date?: Date, format?: string, zone?: string) => {
  if (!date) return '';
  if (!format) format = Meteor.user()?.dateTimeFormat || dateTimeFormatOptions()[0].value;
  if (!format) format = dateTimeFormatOptions()[0].value;
  format = dateFormat(format);
  if (!zone) zone = Meteor.user()?.timezone;
  return DateTime.fromJSDate(date, { zone }).toFormat(format);
};

/**
 * Formats a CALENDAR date: one stored as midnight UTC to stand for a DAY rather than a moment. Every date
 * picked in a date input is written that way (new Date('2024-12-12') parses as UTC midnight), as is every
 * date an integration reads off a document — QuickBooks writes `new Date(`${TxnDate}T00:00:00Z`)`.
 *
 * Rendered in the viewer's own zone such a date names the PREVIOUS DAY everywhere west of UTC, which is
 * the bug this exists to avoid: a paid date of 2024-12-12 read as 12/11/2024 in California. Only the zone
 * is pinned — the locale format is whatever the viewer's would have been.
 *
 * Not for timestamps. A real moment (an audit stamp like completedAt, a createdAt) belongs in the
 * viewer's zone and should use formatDate / formatDateTime.
 * @param date - the calendar date
 * @returns the formatted date, or '' if no date
 */
export const formatCalendarDate = (date?: Date | string): string =>
  (date ? new Date(date).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '');

/**
 * A calendar date as YYYY-MM-DD for an <input type="date">, read in the SAME frame formatCalendarDate
 * displays it in. Reading local components instead puts the previous day in the picker west of UTC, and
 * saving without touching it then writes that day back — a date that walks backwards one edit at a time.
 * @param date - the calendar date
 * @returns YYYY-MM-DD, or '' if no date
 */
export const calendarDateInputValue = (date?: Date | string): string =>
  (date ? new Date(date).toISOString().slice(0, 10) : '');

/**
 * Parses an <input type="date"> value back to the stored form: midnight UTC of that day. Explicit about
 * the zone because `new Date('2026-05-10T00:00:00')` (no Z) is LOCAL midnight, a different instant and a
 * different day once rendered anywhere else.
 * @param value - YYYY-MM-DD from a date input
 * @returns the calendar date, or undefined when blank
 */
export const parseCalendarDate = (value?: string): Date | undefined =>
  (value ? new Date(`${value}T00:00:00Z`) : undefined);

/**
 * Formats the time portion using the user's preferred format and timezone.
 * @param date - the date
 * @param format - datetime format token (time portion is derived)
 * @param zone - timezone (defaults to user timezone)
 * @returns the formatted time
 */
export const formatTime = (date: Date, format?: string, zone?: string) => {
  if (!format) format = Meteor.user()?.dateTimeFormat || dateTimeFormatOptions()[0].value;
  if (!format) format = dateTimeFormatOptions()[0].value;
  format = timeFormat(format);
  if (!zone) zone = Meteor.user()?.timezone;
  return DateTime.fromJSDate(date, { zone }).toFormat(format);
};

/**
 * Maps a combined datetime format to its luxon date-only format.
 * @param dateTimeFormat - the combined format token
 * @returns the date-only format token
 */
export const dateFormat = (dateTimeFormat: string) => {
  const mapping: { [key: string]: string } = {
    'LL/dd/yyyy h:mm a': 'LL/dd/yyyy',
    'LL/dd/yyyy HH:mm': 'LL/dd/yyyy',
    'dd/LL/yyyy h:mm a': 'dd/LL/yyyy',
    'dd/LL/yyyy HH:mm': 'dd/LL/yyyy',
    'dd/LL/yyyy HH \'h\' mm': 'dd/LL/yyyy',
    'yyyy-LL-dd h:mm a': 'yyyy-LL-dd',
    'yyyy-LL-dd HH:mm': 'yyyy-LL-dd',
    'yyyy-LL-dd HH \'h\' mm': 'yyyy-LL-dd',
  };
  return mapping[dateTimeFormat] || 'LL/dd/yyyy';
};

/**
 * Maps a combined datetime format to its luxon time-only format.
 * @param dateTimeFormat - the combined format token
 * @returns the time-only format token
 */
export const timeFormat = (dateTimeFormat: string) => {
  const mapping: { [key: string]: string } = {
    'LL/dd/yyyy h:mm a': 'h:mm a',
    'LL/dd/yyyy HH:mm': 'HH:mm',
    'dd/LL/yyyy h:mm a': 'h:mm a',
    'dd/LL/yyyy HH:mm': 'HH:mm',
    'dd/LL/yyyy HH \'h\' mm': 'HH \'h\' mm',
    'yyyy-LL-dd h:mm a': 'h:mm a',
    'yyyy-LL-dd HH:mm': 'HH:mm',
    'yyyy-LL-dd HH \'h\' mm': 'HH \'h\' mm',
  };
  return mapping[dateTimeFormat] || 'h:mm a';
};

/**
 * Maps a combined datetime format to its moment date-only format.
 * @param dateTimeFormat - the combined format token
 * @returns the moment date-only format token
 */
export const momentDateFormat = (dateTimeFormat: string) => {
  const mapping: { [key: string]: string } = {
    'LL/dd/yyyy h:mm a': 'MM/DD/YYYY',
    'LL/dd/yyyy HH:mm': 'MM/DD/YYYY',
    'dd/LL/yyyy h:mm a': 'DD/MM/YYYY',
    'dd/LL/yyyy HH:mm': 'DD/MM/YYYY',
    'dd/LL/yyyy HH \'h\' mm': 'DD/MM/YYYY',
    'yyyy-LL-dd h:mm a': 'YYYY-MM-DD',
    'yyyy-LL-dd HH:mm': 'YYYY-MM-DD',
    'yyyy-LL-dd HH \'h\' mm': 'YYYY-MM-DD',
  };
  return mapping[dateTimeFormat] || 'MM/DD/YYYY';
};

/**
 * Maps a combined datetime format to its moment time-only format.
 * @param dateTimeFormat - the combined format token
 * @returns the moment time-only format token
 */
export const momentTimeFormat = (dateTimeFormat: string) => {
  const mapping: { [key: string]: string } = {
    'LL/dd/yyyy h:mm a': 'h:mm A',
    'LL/dd/yyyy HH:mm': 'HH:mm',
    'dd/LL/yyyy h:mm a': 'h:mm A',
    'dd/LL/yyyy HH:mm': 'HH:mm',
    'dd/LL/yyyy HH \'h\' mm': 'HH [h] mm',
    'yyyy-LL-dd h:mm a': 'h:mm A',
    'yyyy-LL-dd HH:mm': 'HH:mm',
    'yyyy-LL-dd HH \'h\' mm': 'HH [h] mm',
  };
  return mapping[dateTimeFormat] || 'LL/dd/yyyy';
};

/**
 * Parses a time-of-day string into a moment on the epoch date.
 * @param value - the time string
 * @param props - whether seconds/millis are present in the input
 * @returns the moment, or undefined if no value
 */
export const timeStringToMoment = (value: string | undefined, props?: { includeSeconds?: boolean, includeMillis?: boolean }) => {
  if (!value) return;
  let dateString = `1970-01-01 ${value}`;
  if (!props?.includeSeconds) dateString += ':00';
  if (!props?.includeMillis) dateString += '.000';
  return moment(new Date(dateString));
};

/**
 * Today at midnight local time.
 * @returns the date
 */
export const todaysDate = () => {
  const d = new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
};

/**
 * Tomorrow at midnight local time.
 * @returns the date
 */
export const tomorrowsDate = () => {
  const d = new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  d.setDate(d.getDate() + 1);
  return d;
};

/**
 * First day of the month at midnight for the given date (or now).
 * @param date - reference date
 * @returns the date
 */
export const firstDateOfMonth = (date?: Date) => {
  const d = date && new Date(date.toISOString()) || new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  d.setDate(1);
  return d;
};

/**
 * Last day of the month at midnight for the given date (or now).
 * @param date - reference date
 * @returns the date
 */
export const lastDateOfMonth = (date?: Date) => {
  const d = date && new Date(date.toISOString()) || new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d;
};

/**
 * Sunday of the week at midnight for the given date (or now).
 * @param date - reference date
 * @returns the date
 */
export const firstDateOfWeek = (date?: Date) => {
  const d = date && new Date(date.toISOString()) || new Date();
  const day = d.getDay();
  const firstDay = 0;
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  d.setDate(d.getDate() + firstDay - day);
  return d;
};

/**
 * One week after the start of the week for the given date (or now).
 * @param date - reference date
 * @returns the date
 */
export const lastDateOfWeek = (date?: Date) => {
  let d = date && new Date(date.toISOString()) || new Date();
  d = firstDateOfWeek(d);
  d.setDate(d.getDate() + 7);
  return d;
};

/**
 * Start of the hour for the given date (or now).
 * @param date - reference date
 * @returns the date
 */
export const topOfHour = (date?: Date) => {
  const d = date && new Date(date.toISOString()) || new Date();
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
};

/**
 * Promisified Meteor.call.
 * @param method - method name
 * @param args - method arguments
 * @returns the method result
 */
export const meteorCall = <T = any>(method: string, ...args: any[]) => {
  return new Promise<T>((resolve, reject) => {
    Meteor.call(method, ...args, (error: any, result: any) => {
      if (error) reject(error);
      else resolve(result);
    });
  });
};

/**
 * Base64-encodes a Uint8Array (chunked to avoid call-stack limits).
 * @param u8Arr - the bytes
 * @returns the base64 string
 */
export const uInt8ToBase64 = (u8Arr: Uint8Array) => {
  const CHUNK_SIZE = 0x8000;
  const length = u8Arr.length;
  let index = 0;
  let result = '';
  let slice;
  while (index < length) {
    slice = u8Arr.subarray(index, Math.min(index + CHUNK_SIZE, length));
    result += String.fromCharCode.apply(null, Array.from(slice));
    index += CHUNK_SIZE;
  }
  return window.btoa(result);
};

/**
 * Converts a data URL into a File.
 * @param dataurl - the data URL
 * @param filename - name for the resulting file
 * @returns the File
 */
export const dataUrlToFile = (dataurl: string, filename: string) => {
  const arr = dataurl.split(',');
  const mime = arr[0]?.match(/:(.*?);/)?.[1];
  const bstr = window.atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

/**
 * Formats a byte count as a human-readable size.
 * @param bytes - number of bytes
 * @param decimals - decimal places
 * @returns the formatted size string
 */
export const formatBytes = (bytes: number, decimals = 0) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Triggers a browser download of in-memory data.
 * @param data - blob contents
 * @param filename - download filename
 * @param type - MIME type
 */
export const clientDownload = (data: any, filename: string, type: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([data], { type }));
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Triggers a browser download from a URL.
 * @param url - source URL
 * @param filename - download filename (defaults to URL basename)
 */
export const clientDownloadUrl = (url: string, filename?: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', filename || url.split('/').at(-1) as string);
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Creates an anchor with the given attributes, clicks it, and removes it.
 * @param href - anchor href
 * @param props - additional attributes to set
 */
export const appendAndClickAnchor = (href: string, props?: any) => {
  const a = document.createElement('a');
  a.href = href;
  for (const p in props || {}) a.setAttribute(p, props?.[p]);
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const publicPaths = [
  '/login',
  '/signup',
  '/signup-submitted',
  '/apply',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/terms',
  '/privacy',
  '/google-oauth2-redirect',
  '/discord-oauth2-redirect',
  '/cea-proposal',
  '/cea-affiliate-detailed-ess-solar-proposal',
  '/cea-affiliate-detailed-ess-proposal',
  '/cea-affiliate-indicative-ess-solar-proposal',
  '/cea-affiliate-indicative-ess-proposal',
  '/cea-dealer-indicative-ess-solar-proposal',
  '/cea-dealer-indicative-ess-proposal',
  '/cea-dealer-detailed-ess-solar-proposal',
  '/cea-dealer-detailed-ess-proposal',
  '/shared',
  '/enphase-redirect',
  '/quickbooks/connected',
  '/solar-bill-view',
  '/view-solar-bills',
  '/utility-account-intake',
  '/uail',
  '/uai',
  '/sma-redirect',
  '/accept-invitation',
];

/**
 * Reports whether a path is publicly accessible (no auth required).
 * @param pathname - path to check (defaults to current location)
 * @returns true if public
 */
export const isPublicPath = (pathname?: string) => {
  pathname = pathname || window.location.pathname;
  return publicPaths.includes(pathname) || pathname.startsWith('/signup');
};

/** Composable array comparator factory (vendored from thenBy.js). */
export const firstBy = (function () {
  // This was stolen from https://github.com/Teun/thenBy.js
  function identity(v: any) { return v; }

  function ignoreCase(v: any) { return typeof (v) === 'string' ? v.toLowerCase() : v; }

  function makeCompareFunction(f: any, opt: any) {
    opt = typeof (opt) === 'object' ? opt : { direction: opt };
    if (typeof (f) != 'function') {
      const prop = f;
      f = function (v1: any) { return v1[prop] ? v1[prop] : ''; };
    }
    if (f.length === 1) {
      const uf = f;
      const preprocess = opt.ignoreCase ? ignoreCase : identity;
      const cmp = opt.cmp || function (v1: any, v2: any) { return v1 < v2 ? -1 : v1 > v2 ? 1 : 0; };
      f = function (v1: any, v2: any) { return cmp(preprocess(uf(v1)), preprocess(uf(v2))); };
    }
    const descTokens = { '-1': '', desc: '' };
    if (opt.direction in descTokens) return function (v1: any, v2: any) { return -f(v1, v2); };
    return f;
  }

  function tb(this: any, func: any, opt: any) {
    const x = (typeof (this) == 'function' && !this.firstBy) ? this : false;
    const y = makeCompareFunction(func, opt);
    const f = x ? function (a: any, b: any) {
      return x(a, b) || y(a, b);
    }
      : y;
    f.thenBy = tb;
    return f;
  }
  tb.firstBy = tb;
  return tb;
})();

/**
 * Returns the array with duplicates removed.
 * @param array - source array
 * @returns deduplicated array
 */
export const uniqueArray = <T>(array: T[]) => {
  return Array.from(new Set(array));
};

/**
 * Filters out null and undefined entries.
 * @param array - source array
 * @returns array without nullish entries
 */
export const removeNullish = <T>(array: (T | undefined | null)[]) => {
  return array.filter((a) => a !== null && a !== undefined) as T[];
};

/**
 * Center-crops an image file to a square then scales it to the target size.
 * @param file - source image file
 * @param width - output width
 * @param height - output height
 * @returns a JPEG File (or the original on failure)
 */
export const cropImageCenter = async (file: File, width: number, height: number) => {
  const reader = new FileReader();
  const image = await new Promise<HTMLImageElement>((resolve) => {
    reader.addEventListener('load', () => {
      const img = new Image();
      img.title = file.name;
      img.src = reader.result as string;
      resolve(img);
    });
    reader.readAsDataURL(file);
  });
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  let sx = 0;
  let sy = 0;
  let sw = image.width;
  let sh = image.height;
  if (image.width > image.height) {
    sx = (image.width - image.height) / 2;
    sw = sh;
  }
  else {
    sy = (image.height - image.width) / 2;
    sh = sw;
  }
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
  return await new Promise<File>((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) return resolve(file);
      const _file = new File([blob], file.name, { type: 'image/jpeg' });
      resolve(_file);
    }, 'image/jpeg');
  });
};

/** Password validation regex. */
export const passwordRegex = () => {
  return /^(?=.*\d)(?=.*[A-Z])(?!.*[^a-zA-Z0-9])(.{8})$/g;
};

/**
 * Ordinal suffix for a date's day of month.
 * @param date - the date
 * @returns 'st', 'nd', 'rd', or 'th'
 */
export const dateSuffix = (date: Date) => {
  const digits = `${date.getDate()}`;
  switch (digits) {
    case '1': return 'st';
    case '2': return 'nd';
    case '3': return 'rd';
    case '21': return 'st';
    case '22': return 'nd';
    case '23': return 'rd';
    case '31': return 'st';
    default: return 'th';
  }
};

/**
 * Parses a query string into a flat key/value object (first value wins per key).
 * @param queryString - the query string (defaults to current location.search)
 * @returns the parsed params
 */
export const queryStringToObject = (queryString?: string): Record<string, string> => {
  queryString = (queryString || window.location.search || '?').trim();
  if (queryString.startsWith('?')) queryString = queryString.substring(1);
  const params = new URLSearchParams(queryString);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    if (result[key]) return;
    else result[key] = value;
  });
  return result;
};

/**
 * Captures the current stack trace as a string.
 * @returns the trace text
 */
export const stackTrace = () => {
  const obj: any = {};
  Error.captureStackTrace(obj, stackTrace);
  return obj.stack.substr(7);
};

/**
 * Zero-pads a number to two digits.
 * @param number - the number
 * @returns the padded string
 */
export const zeroPadded = (number: number) => {
  return number > 9 ? `${number}` : `0${number}`;
};

/**
 * Indexes documents by their _id.
 * @param arr - documents
 * @returns map of _id to document
 */
export const mapById = <T extends { _id: string }>(arr: T[]) => {
  return arr.reduce((map: { [key: string]: T }, doc) => {
    map[doc._id] = doc;
    return map;
  }, {});
};

/**
 * Indexes items by a key's value (last item wins per value).
 * @param arr - items
 * @param key - key to index by
 * @returns map of value to item
 */
export const mapByKey = <T>(arr: T[], key: keyof T) => {
  return arr.reduce((map: { [key: string]: T }, doc) => {
    map[doc[key] as string] = doc;
    return map;
  }, {});
};

/**
 * Groups items into arrays keyed by a key's value.
 * @param arr - items
 * @param key - key to group by
 * @returns map of value to items
 */
export const mapArrByKey = <T>(arr: T[], key: keyof T) => {
  return arr.reduce((map: { [key: string]: T[] }, doc) => {
    const v = doc[key] as string;
    if (!map[v]) map[v] = [];
    map[v].push(doc);
    return map;
  }, {});
};

/**
 * Reports whether a user agent looks like a mobile device.
 * @param userAgent - user agent string (defaults to navigator.userAgent)
 * @returns true if mobile
 */
export const isMobile = (userAgent?: string) => {
  userAgent = userAgent || window.navigator.userAgent;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
};

type CsvOpts = {
  keys?: string[],
  headerMap?: { [key: string]: string },
  maps?: {
    [key: string]: (value: any, obj: any) => string,
  },
  prepend?: any[][],
  append?: any[][],
};

/**
 * Serializes an array of objects to CSV.
 * @param array - rows
 * @param opts - keys, header labels, per-column value maps, and append rows
 * @returns the CSV string
 */
export const arrayToCsv = (array: any[], opts: CsvOpts = {}) => {
  if (!array.length) return '';
  const keys = opts.keys || Object.keys(array[0]);
  const headerMap = opts.headerMap || {};
  const maps = opts.maps || {};
  const header = keys.map((h) => headerMap[h] || h);
  let data = [header].concat(array.map((obj) => {
    return keys.map((k) => maps[k]?.(obj[k], obj) || obj[k]);
  }));
  if (opts.append) data = data.concat(opts.append);
  return stringify(data);
};

/**
 * Capitalizes the first letter of each space-separated word.
 * @param string - input string
 * @returns the capitalized string
 */
export const capitalize = (string: string) => {
  return string.split(' ').map((word) => `${word.charAt(0).toUpperCase()}${word.substring(1)}`).join(' ');
};

/**
 * Computes the offset in ms between a date and its rendering in a timezone.
 * @param date - the date
 * @param timeZone - IANA timezone name
 * @returns the offset in milliseconds
 */
export const timezoneOffset = (date: Date, timeZone: string) => {
  const invdate = new Date(date.toLocaleString('en-US', { timeZone }));
  const diff = date.getTime() - invdate.getTime();
  return diff;
};

/**
 * Base64-encodes a string (client or server).
 * @param string - input string
 * @returns the base64 string
 */
export const stringToBase64 = (string: string) => {
  return Meteor.isClient ? window.btoa(string) : Buffer.from(string).toString('base64');
};

/**
 * Decodes a base64 string (client or server).
 * @param base64 - base64 input
 * @returns the decoded string
 */
export const base64ToString = (base64: string) => {
  return Meteor.isClient ? window.atob(base64) : Buffer.from(base64, 'base64').toString('utf8');
};

/**
 * Computes a simple moving average over a window.
 * @param data - input series
 * @param width - window width
 * @returns the smoothed series
 */
export const movingAverage = (data: number[], width: number) => {
  const calculateItemsSum = (data: number[], start: number, stop: number) => {
    let sum = 0;
    for (let j = start; j < stop; ++j) sum += data[j];
    return sum;
  };
  const steps = data.length - width;
  const result = [];
  for (let i = 0; i < steps; ++i) {
    const sum = calculateItemsSum(data, i, i + width);
    result.push(sum / width);
  }
  return result;
};

/** Sum of an array of numbers. */
export const sum = (data: number[]) => data.reduce((t, n) => t + n, 0);
/** Mean of an array of numbers. */
export const avg = (data: number[]) => data.reduce((t, n) => t + n, 0)/(data.length || 1);

/**
 * Reports whether a string is valid JSON.
 * @param json - the candidate string
 * @returns true if parseable
 */
export const isValidJson = (json: string) => {
  try {
    JSON.parse(json);
    return true;
  }
  catch (_) {
    return false;
  }
};

/**
 * Computes a fast 32-bit djb2-style hash of a string.
 * @param s - input string
 * @returns the hash
 */
export const hash = (s: string) => s.split('').reduce((a: number, b: string) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);

export const chartColors = [
  '#6A9BC8', '#448F9C', '#ECAF4B', '#BD2D40', '#109E67', '#E67382', '#663438'
];

/** Random hex color. */
export const randomColor = () => `#${Math.floor(Math.random() * 16777215).toString(16)}`;

/**
 * Reports whether a value is iterable.
 * @param obj - value to test
 * @returns true if iterable
 */
export const isIterable = (obj: any) => {
  return !!obj && typeof obj[Symbol.iterator] === 'function';
};

/**
 * Flattens a nested object into dot-delimited keys.
 * @param obj - source object
 * @param prefix - key prefix for recursion
 * @returns the flattened object
 */
export const flattenObject = (obj: any, prefix = '') => {
  return Object.keys(obj).reduce((acc: any, k) => {
    const pre = prefix.length ? prefix + '.' : '';
    const v = obj[k];
    if (v && typeof v === 'object') Object.assign(acc, flattenObject(v, pre + k));
    else acc[pre + k] = v;
    return acc;
  }, {});
};

/**
 * Rebuilds a nested object from dot-delimited keys.
 * @param object - flattened object
 * @returns the nested object
 */
export const unflattenObject = (object: any) => {
  const dottedKeys: string[] = [];
  const structured: any = {};
  const keys = objectKeys(object).sort((a, b) => a.length - b.length);
  for (const k of keys) {
    const parts = k.split('.');
    const lminus1 = parts.length - 1;
    let pointer: any = structured;
    let partsJoined = '';
    for (const [i, p] of parts.entries()) {
      partsJoined += i === 0 ? p : `.${p}`;
      if (dottedKeys.includes(partsJoined) && typeof pointer !== 'object') break;
      ensureProperty(pointer, p, i === lminus1 ? object[k] : {});
      pointer = pointer[p];
    }
  }
  return structured;
};

/**
 * Returns an object's property, initializing it if undefined.
 * @param object - target object
 * @param key - property key
 * @param initial - value to set when missing
 * @returns the existing or initialized value
 */
export const ensureProperty = (object: any, key: string, initial: any) => {
  const value = object[key];
  if (value === undefined) {
    object[key] = initial;
    return initial;
  }
  return value;
};

/**
 * Rounds a number to a given number of decimal places.
 * @param number - the number
 * @param decimalPlaces - decimal places
 * @returns the rounded number
 */
/** Escapes a string for use inside a RegExp — an email in a case-insensitive lookup, say. */
export const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const roundNumber = (number: number, decimalPlaces = 0) => {
  const power = 10 ** decimalPlaces;
  return Math.round(power * number) / power;
};

/**
 * Splits an amount across weights, in cents, so the shares add up to exactly the amount: each share is
 * rounded and the last positive weight absorbs the rounding remainder.
 * @param total - the amount to split
 * @param weights - the proportions (percentages, an allocation's amounts, a bill's line amounts)
 * @returns one share per weight
 */
export const splitProportionally = (total: number, weights: number[]): number[] => {
  const sum = weights.reduce((acc, w) => acc + (w > 0 ? w : 0), 0);
  if (!(sum > 0)) return weights.map(() => 0);
  const shares = weights.map((w) => (w > 0 ? roundNumber(total * w / sum, 2) : 0));
  const last = weights.map((w, i) => (w > 0 ? i : -1)).filter((i) => i >= 0).pop();
  if (last !== undefined) shares[last] = roundNumber(total - shares.reduce((acc, x, i) => acc + (i === last ? 0 : x), 0), 2);
  return shares;
};

/**
 * Formats a number with thousands separators and fixed decimals.
 * @param number - the number (undefined renders as '-')
 * @param decimalPlaces - decimal places
 * @returns the formatted string
 */
export const formatNumber = (number?: number, decimalPlaces = 0) => {
  if (number === undefined) return '-';
  const roundedNumber = roundNumber(number, decimalPlaces);

  const string = roundedNumber.toString();
  const [integer, decimal] = string.split('.');

  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  let formattedDecimal = decimal || '';
  while (formattedDecimal.length < decimalPlaces) {
    formattedDecimal += '0';
  }

  const formattedNumber = `${formattedInteger}${formattedDecimal.length ? '.' : ''}${formattedDecimal}`;

  return formattedNumber;
};

/**
 * Formats a US phone number as (xxx) xxx-xxxx.
 * @param phone - raw phone string
 * @returns the formatted number, or the input if not 10 digits
 */
export const formatPhone = (phone?: string) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  const d = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (d.length !== 10) return phone;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

/**
 * Formats a number as USD currency.
 * @param number - the amount
 * @param decimals - decimal places
 * @returns the formatted currency string
 */
export const formatCurrency = (number: number, decimals = 2) => {
  return `$${formatNumber(number, decimals)}`;
};

/**
 * Enters print mode, opens the browser print dialog, and restores on close.
 * @param onClose - callback after printing finishes
 */
export const openPrintDialog = (onClose?: () => void) => {
  startPrintMode();
  window.addEventListener('afterprint', () => {
    stopPrintMode();
    onClose?.();
  }, { once: true });
  setTimeout(() => { print(); }, 1000);
};

/** Adds the print-mode class to all elements. */
export const startPrintMode = () => {
  document.querySelectorAll('*').forEach((el) => {
    el.classList.add('print-mode');
  });
};

/** Removes the print-mode class from all elements. */
export const stopPrintMode = () => {
  document.querySelectorAll('*').forEach((el) => {
    el.classList.remove('print-mode');
  });
};

/**
 * Appends a query string built from params to a URL.
 * @param url - base URL
 * @param params - query params
 * @returns the URL with query string
 */
export const urlWithParams = (url: string, params: { [key: string]: string }) => {
  const searchParams = new URLSearchParams(params);
  return `${url}?${searchParams.toString()}`;
};

/**
 * Splits a date range into consecutive sub-ranges of a fixed interval.
 * @param fromDate - range start
 * @param toDate - range end
 * @param interval - sub-range length in ms
 * @returns array of {fromDate, toDate} ranges
 */
export const splitDateRange = (fromDate: Date, toDate: Date, interval: number) => {
  const fromDateStamp = fromDate.getTime();
  const toDateStamp = toDate.getTime();
  const dateRanges: { fromDate: Date, toDate: Date }[] = [];
  let cursor = fromDateStamp;
  while (cursor < toDateStamp) {
    const fromDate = new Date(cursor);
    const toStamp = Math.min(cursor + interval, toDateStamp);
    const toDate = new Date(toStamp);
    dateRanges.push({ fromDate, toDate });
    cursor = toDate.getTime();
  }
  return dateRanges;
};

/**
 * Deletes undefined-valued keys from an object in place.
 * @param obj - the object
 * @returns the same object
 */
export const stripUndefinedFields = (obj: any) => {
  if (typeof obj === 'object') {
    for (const key in obj) {
      if (obj[key] === undefined) delete obj[key];
    }
  }
  return obj;
};

export const stateCodeMap: { [key: string]: string } = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

/**
 * Logs with an ISO timestamp prefix.
 * @param data - primary value
 * @param args - additional values
 */
export const consoleLog = (data: any, ...args: any[]) => {
  console.log(`${(new Date()).toISOString()} [console]`, data, ...args);
};

/**
 * Resolves after a delay.
 * @param millis - delay in milliseconds
 */
export const sleep = async (millis: number) => {
  await new Promise((resolve) => {
    setTimeout(() => resolve(null), millis);
  });
};

/**
 * Retries an async function with a delay between attempts.
 * @param fn - function receiving the attempt number
 * @param opts - max attempts and sleep between attempts
 * @returns the function's result
 */
export const retry = async <T>(fn: (attempt: number) => Promise<T>, opts?: { maxAttempts?: number, sleep?: number }): Promise<T> => {
  const maxAttempts = opts?.maxAttempts ?? 3;
  const sleepMs = opts?.sleep ?? 3000;
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn(attempt);
    } 
    catch (error) {
      if (attempt >= maxAttempts) throw error;
      await sleep(sleepMs);
    }
  }
};

const allowedOps: Dict<RegExp> = {
  and: /"\$and":/g,
  or: /"\$or":/g,
  exists: /"\$exists":/g,
  in: /"\$in":/g,
  nin: /"\$nin":/g,
  gt: /"\$gt":/g,
  gte: /"\$gte":/g,
  lt: /"\$lt":/g,
  lte: /"\$lte":/g,
  ne: /"\$ne":/g,
  date: /"\$date":/g,
};

/**
 * Strips disallowed `$` operators from a Mongo selector, preserving an allowlist.
 * @param selector - the query selector
 * @returns the sanitized selector
 */
export const mongoSanitize = <T>(selector: Mongo.Query<T>) => {
  let str = EJSON.stringify(selector);
  for (const op in allowedOps) {
    const re = allowedOps[op];
    str = str.replace(re, `"#${op}":`);
  }
  str = str.replace(/\$/g, '');
  str = str.replace(/#/g, '$');
  return EJSON.parse(str) as Mongo.Query<T>;
};

/**
 * Builds a renderer that resolves a reference key to a display string.
 * @param modelClass - the owning model class
 * @param key - the reference field key
 * @returns a function mapping (doc, references) to a display string
 */
export const referenceRender = <T extends Docmnt>(modelClass: ModelClass<T>, key: string) => {
  const relatedClass = modelClass.references?.[key]?.modelClass;
  return (p: T, r: Dict<Dict<Docmnt>>) => {
    if (!p[key]) return '';
    const relatedDoc = r?.[key]?.[p[key] as string];
    if (relatedClass?.asString) return relatedClass?.asString?.(relatedDoc);
    return relatedDoc?.name || relatedDoc?._id || '';
  };
};

/**
 * Resolves a document's reference key to a display string.
 * @param modelClass - the owning model class
 * @param document - the document
 * @param key - the reference field key
 * @returns the display string
 */
export const referenceString = <T extends Docmnt>(modelClass: ModelClass<T>, document: T, key: string) => {
  const relatedClass = modelClass.references?.[key]?.modelClass;
  const value = document[key] as string;
  if (!value) return '';
  const r = document.$references;
  const relatedDoc = r?.[key]?.[value];
  if (relatedClass?.asString) return relatedClass?.asString?.(relatedDoc);
  return relatedDoc?.name || relatedDoc?._id || '';
};

/**
 * Returns an array of integers 0..n-1.
 * @param n - length
 * @returns the index array
 */
export const range = (n: number) => {
  return [...Array(n).keys()];
};

/**
 * Random integer in [min, max).
 * @param min - lower bound (inclusive)
 * @param max - upper bound (exclusive)
 * @returns the random integer
 */
export const randomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min);
};

/**
 * Reports whether a password has a number, letter, and special character.
 * @param password - the password
 * @returns true if all rules pass
 */
export const checkPasswordRules = (password = '') => {
  const hasNumber = /\d/.test(password);
  const hasLetter = /[a-z]/i.test(password);
  const hasSpecialCharacter = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password);
  return hasNumber && hasLetter && hasSpecialCharacter;
};

const emailRegex = /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

/**
 * Tests an email against the email regex.
 * @param email - the email
 * @returns true if it matches
 */
export const validateEmail = (email = '') => {
  return emailRegex.test(email);
};

/**
 * Validates an email's structure and part lengths.
 * @param email - the email
 * @returns true if valid
 */
export const isValidEmail = (email?: string) => {
  if (!email) return false;
  const emailParts = email.split('@');
  if (emailParts.length !== 2) return false;
  const account = emailParts[0];
  const address = emailParts[1];
  if (account.length > 64) return false;
  else if (address.length > 255) return false;
  const domainParts = address.split('.');
  if (domainParts.some((part) => {
    return part.length > 63;
  })) return false;
  return emailRegex.test(email);
};

/**
 * Creates a rate limiter that runs at most N tasks per interval.
 * @param maxRequestsPerInterval - max tasks per interval
 * @param interval - interval length in ms
 * @param evenlySpaced - if true, space tasks evenly across the interval
 * @returns a function that enqueues a task and resolves with its result
 */
export const throttleFunction = (
  maxRequestsPerInterval: number,
  interval: number,
  evenlySpaced = false,
) => {
  if (evenlySpaced) {
    interval = interval / maxRequestsPerInterval;
    maxRequestsPerInterval = 1;
  }
  const queue: Array<() => Promise<void>> = [];
  let lastIntervalStart = 0;
  let numRequestsPerInterval = 0;
  let timeout: NodeJS.Timeout | undefined;
  const dequeue = () => {
    const intervalEnd = lastIntervalStart + interval;
    const now = Date.now();
    if (now < intervalEnd) {
      timeout !== undefined && clearTimeout(timeout);
      timeout = setTimeout(dequeue, intervalEnd - now);
      return;
    }
    lastIntervalStart = now;
    numRequestsPerInterval = 0;
    for (const callback of queue.splice(0, maxRequestsPerInterval)) {
      numRequestsPerInterval++;
      void callback();
    }
    if (queue.length) timeout = setTimeout(dequeue, interval);
    else timeout = undefined;
  };

  return <Return = unknown>(fn: () => Promise<Return> | Return): Promise<Return> => new Promise<Return>(
    (resolve, reject) => {
      const callback = () => Promise.resolve().then(fn).then(resolve).catch(reject);
      const now = Date.now();
      if (timeout === undefined && (now - lastIntervalStart) > interval) {
        lastIntervalStart = now;
        numRequestsPerInterval = 0;
      }
      if (numRequestsPerInterval++ < maxRequestsPerInterval) {
        void callback();
      }
      else {
        queue.push(callback);
        if (timeout === undefined) {
          timeout = setTimeout(dequeue, lastIntervalStart + interval - now);
        }
      }
    },
  );
};

/**
 * Recursively merges objects/arrays into a deep clone.
 * @param objs - objects to merge left to right
 * @returns the merged result
 */
export const deepMerge = (...objs: any[]) => {
  const getType = (obj: any) => {
    return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
  };
  const mergeObj = (clone: any, obj: any) => {
    for (const [key, value] of objectEntries(obj)) {
      const type = getType(value);
      if (clone[key] !== undefined && getType(clone[key]) === type && ['array', 'object'].includes(type)) {
        clone[key] = deepMerge(clone[key], value);
      } 
      else {
        clone[key] = structuredClone(value);
      }
    }
  };
  let clone = structuredClone(objs.shift());
  for (const obj of objs) {
    const type = getType(obj);
    if (getType(clone) !== type) {
      clone = structuredClone(obj);
      continue;
    }
    if (type === 'array') clone = [...clone, ...structuredClone(obj)];
    else if (type === 'object') mergeObj(clone, obj);
    else clone = obj;
  }
  return clone;
};

/**
 * Dispatches synthetic mouse events to click an element, with optional jitter.
 * @param selector - CSS selector or element
 * @param jitter - random offset factor for the click point
 */
export const simulateClick = async (selector: string | HTMLElement, jitter = 0) => {
  const element = typeof selector === 'string' ? document.querySelector(selector) as HTMLElement : selector;
  if (!element) return;
  const box = element.getBoundingClientRect();
  const jitterX = jitter * Math.random() * box.width / 2 * (Math.random() > 0.5 ? 1 : -1);
  const jitterY = jitter * Math.random() * box.height / 2 * (Math.random() > 0.5 ? 1 : -1);
  const coordX = box.left + (box.right - box.left) / 2 + jitterX;
  const coordY = box.top + (box.bottom - box.top) / 2 + jitterY;
  const simulateMouseEvent = (element: HTMLElement, eventName: string, coordX: number, coordY: number) => {
    element.dispatchEvent(new MouseEvent(eventName, {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX: coordX,
      clientY: coordY,
      button: 0
    }));
  };
  simulateMouseEvent(element, 'mousedown', coordX, coordY);
  simulateMouseEvent(element, 'mouseup', coordX, coordY);
  simulateMouseEvent(element, 'click', coordX, coordY);
};

/**
 * Fetches the client's IP address from the server.
 * @returns the IP address
 */
export const getIpAddress = async () => {
  const response = await http.get(`//${window.location.host}/clientip`);
  return response.data.ip;
};

/**
 * Looks up geolocation for an IP address.
 * @param ipAddress - the IP (defaults to the caller's)
 * @returns the geo result and an {lat, lng} origin
 */
export const getGeoInfo = async (ipAddress?: string) => {
  const result = await geoIp.call(ipAddress);
  const origin = { lat: result.location.latitude, lng: result.location.longitude };
  return { result, origin };
};

/**
 * Attaches resolved reference documents to each document under `$references`.
 * @param documents - the documents
 * @param $references - map of reference key to id-keyed related docs
 * @returns documents with `$references` populated
 */
export const attachReferences = <T extends Docmnt, R extends RefMap<T>>(documents: T[], $references: { [k in keyof R]: Dict<R[k]> }) => {
  if (!$references) return documents;
  const refKeys = objectKeys($references);
  return documents.map((doc) => {
    const _doc = { ...doc, $references: {} };
    for (const key of refKeys) {
      const refId = doc[key] as string;
      const refDoc = $references[key]?.[refId];
      if (!_doc.$references[key]) _doc.$references[key] = {};
      if (refDoc) _doc.$references[key][refId] = refDoc;
    }
    return _doc;
  });
};

/**
 * Extracts the host from a URL.
 * @param url - the URL
 * @returns the host
 */
export const urlHost = (url: string) => {
  return (new URL(url)).host;
};

/**
 * Returns elements present in both arrays.
 * @param arr1 - first array
 * @param arr2 - second array
 * @returns the intersection
 */
export const arrayIntersection = <T>(arr1: T[], arr2: T[]) => {
  return arr1.filter((x) => arr2.includes(x));
};

/**
 * Reads a localStorage value, JSON-parsing when possible.
 * @param key - storage key
 * @returns the parsed value, raw string, or null
 */
export const localStorageGet = (key: string) => {
  const value = localStorage.getItem(key);
  try {
    return JSON.parse(value || '');
  }
  catch (_) {
    return value;
  }
};

/**
 * Writes a value to localStorage, JSON-stringifying when possible.
 * @param key - storage key
 * @param value - value to store
 */
export const localStorageSet = (key: string, value: any) => {
  try {
    return localStorage.setItem(key, JSON.stringify(value));
  }
  catch (_) {
    return localStorage.setItem(key, value);
  }
};

/**
 * Removes a localStorage key.
 * @param key - storage key
 */
export const localStorageRemove = (key: string) => {
  return localStorage.removeItem(key);
};

/**
 * Filters out falsy entries.
 * @param array - source array
 * @returns array of truthy entries
 */
export const filterBoolean = <T>(array: (T | undefined | null)[]) => {
  return array.filter(Boolean) as T[];
};

/**
 * Splits an array into chunks of a given size.
 * @param array - source array
 * @param chunkSize - max chunk length
 * @returns array of chunks
 */
export const chunkArray = <T>(array: T[], chunkSize: number) => {
  const res: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    res.push(chunk);
  }
  return res;
};

/**
 * Returns the current Meteor user id, or undefined if unavailable.
 * @returns the user id
 */
export const currentUserId = () => {
  try { return Meteor.userId(); }
  catch (_) { return; }
};

/**
 * Reads a File's contents as text.
 * @param file - the file
 * @param encoding - text encoding
 * @returns the file text
 */
export const readFileAsText = (file: File, encoding?: string) => {
  return new Promise<string | undefined>((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', (e) => resolve(e.target?.result as string));
    reader.readAsText(file, encoding);
  });
};

/**
 * Rounds a date to the nearest multiple of minutes.
 * @param minutes - rounding granularity
 * @param date - the date
 * @returns the rounded date
 */
export const roundDateToMinutes = (minutes: number, date: Date) => {
  const ms = 1000 * 60 * minutes;
  return new Date(Math.round(date.getTime() / ms) * ms);
};

/**
 * Whole years between two dates (order-independent).
 * @param date1 - one date
 * @param date2 - the other date
 * @returns the number of full years
 */
export const numYearsBetween = (date1: Date, date2: Date) => {
  if (date1.getTime() > date2.getTime()) {
    const date = date1;
    date1 = date2;
    date2 = date;
  }
  const startDate = new Date(date1);
  const endDate = new Date(date2);
  let years = endDate.getFullYear() - startDate.getFullYear();
  const monthDiff = endDate.getMonth() - startDate.getMonth();
  const dayDiff = endDate.getDate() - startDate.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) years--;
  return years;
};

/**
 * Fuzzy-matches one string against another via Fuse.js.
 * @param str1 - query string
 * @param str2 - target string
 * @param threshold - Fuse match threshold
 * @returns the Fuse search results
 */
export const fuzzyMatch = (str1: string, str2: string, threshold = 0.3) => {
  const fuse = new Fuse([str2], { includeScore: true, threshold });
  const result = fuse.search(str1);
  return result;
};

/**
 * Removes duplicate vertices from a GeoJSON polygon ring in place.
 * @param r - GeoJSON feature with polygon geometry
 */
export const removeDuplicateVertices = (r: any) => {
  const coordinates = r.geometry.coordinates[0];
  const map = coordinates.slice(0, coordinates.length - 1).reduce((map: any, pair: any) => {
    const key = JSON.stringify(pair);
    if (!map[key]) map[key] = pair;
    return map;
  }, {});
  const cleaned = [...objectValues(map), coordinates[coordinates.length - 1]];
  r.geometry.coordinates = [cleaned];
};

/**
 * Projects WGS84 (EPSG:4326) coordinates to Web Mercator (EPSG:3857).
 * @param lat4326 - latitude in degrees
 * @param lng4326 - longitude in degrees
 * @returns projected {lat, lng} in meters
 */
export const epsg4326To3857 = (lat4326: number, lng4326: number) => {
  const R = 6378137; // EPSG:3857 Earth radius in meters
  const lonRad = (lng4326 * Math.PI) / 180;
  const latRad = (lat4326 * Math.PI) / 180;
  const x = R * lonRad;
  const sinLat = Math.sin(latRad);
  const y = R * Math.log((1 + sinLat) / (1 - sinLat)) / 2;
  return { lat: y, lng: x };
};

/**
 * Builds a value-to-label map from select options.
 * @param options - option list
 * @returns map of value to label
 */
export const optionsToDict = (options: { value: string | number, label: string | null }[]) => {
  return options.reduce((m: Dict<string | null>, o) => {
    m[o.value] = o.label;
    return m;
  }, {});
};

/**
 * Copies text to the clipboard.
 * @param text - text to copy
 */
export const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

const HTML_ESCAPES: { [ch: string]: string } = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' };
/** Escapes text for interpolation into HTML (element content or a quoted attribute). */
export const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);

/**
 * Copies a link to the clipboard AS A LINK: a rich-text editor (an email, a document) pastes `<a href>text</a>`,
 * a plain-text field pastes the URL. Where the browser cannot carry two flavours, the URL alone is copied.
 * @param href - the absolute URL
 * @param text - the link's text
 */
export const copyLinkToClipboard = async (href: string, text: string) => {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return navigator.clipboard.writeText(href);
  const html = `<a href="${escapeHtml(href)}">${escapeHtml(text)}</a>`;
  await navigator.clipboard.write([new ClipboardItem({
    'text/html': new Blob([html], { type: 'text/html' }),
    'text/plain': new Blob([href], { type: 'text/plain' }),
  })]);
};

/**
 * Deep-clones an object via EJSON round-trip.
 * @param obj - the object
 * @returns the clone
 */
export const cloneObject = <T extends EJSON>(obj: T) => EJSON.parse(EJSON.stringify(obj));